/**
 * Bizops capability packs — pillar-aligned verbs.
 * Roles = named pack bundles (ROLE_PACKS). HTTP: @Roles + @RequirePack (PacksGuard).
 * Custom roles store packs on `identity.Role.capabilityPacks`; JWT carries resolved union.
 */

export const CAPABILITY_PACK = {
  CATALOG_MAINTAIN: 'catalog.maintain',
  OFFER_PRICE: 'offer.price',
  OFFER_PROMO: 'offer.promo',
  STOCK_MOVE: 'stock.move',
  BUY_PROCURE: 'buy.procure',
  BUY_PAY: 'buy.pay',
  SELL_COMMIT: 'sell.commit',
  STOREFRONT_MAINTAIN: 'storefront.maintain',
  STOREFRONT_COMMERCE: 'storefront.commerce',
  CRM_LEAD: 'crm.lead',
  CONTENT_PUBLISH: 'content.publish',
  MONEY_READ: 'money.read',
  MONEY_POST: 'money.post',
  PARTY_ADMIN: 'party.admin',
  OPS_VIEW: 'ops.view',
} as const;

export type CapabilityPack = (typeof CAPABILITY_PACK)[keyof typeof CAPABILITY_PACK];

export const ALL_CAPABILITY_PACKS: readonly CapabilityPack[] = Object.values(CAPABILITY_PACK);

/** French labels for admin pack chips (human verbs, same meaning as guards). */
export const CAPABILITY_PACK_LABEL_FR: Record<CapabilityPack, string> = {
  [CAPABILITY_PACK.CATALOG_MAINTAIN]: 'Modifier le catalogue',
  [CAPABILITY_PACK.OFFER_PRICE]: 'Définir les prix',
  [CAPABILITY_PACK.OFFER_PROMO]: 'Promouvoir les offres',
  [CAPABILITY_PACK.STOCK_MOVE]: 'Déplacer le stock',
  [CAPABILITY_PACK.BUY_PROCURE]: 'Commander chez le fournisseur',
  [CAPABILITY_PACK.BUY_PAY]: 'Payer le fournisseur',
  [CAPABILITY_PACK.SELL_COMMIT]: 'Vendre',
  [CAPABILITY_PACK.STOREFRONT_MAINTAIN]: 'Gérer la boutique',
  [CAPABILITY_PACK.STOREFRONT_COMMERCE]: 'Configurer livraison et paiement',
  [CAPABILITY_PACK.CRM_LEAD]: 'Gérer les leads',
  [CAPABILITY_PACK.CONTENT_PUBLISH]: 'Publier le contenu',
  [CAPABILITY_PACK.MONEY_READ]: 'Lire la compta',
  [CAPABILITY_PACK.MONEY_POST]: 'Écrire en compta',
  [CAPABILITY_PACK.PARTY_ADMIN]: 'Gérer comptes et rôles',
  [CAPABILITY_PACK.OPS_VIEW]: 'Consulter',
};

/** Hamssah job-role names (seeded identity.Role.name). */
export const JOB_ROLE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  CATALOGUER: 'CATALOG',
  INVENTORY: 'INVENTORY',
  ACCOUNTING: 'ACCOUNTING',
  SELLER: 'SALES',
  VIEWER: 'VIEWER',
  SHOWROOM_STAFF: 'SHOWROOM_STAFF',
  STOREFRONT: 'STOREFRONT',
  WEB: 'WEB',
} as const;

/**
 * Seeded identity.Role.name → capability packs.
 * ACCOUNTING owns AP pay + money; INVENTORY owns stock + procure.
 */
export const ROLE_PACKS: Record<string, readonly CapabilityPack[]> = {
  SUPER_ADMIN: ALL_CAPABILITY_PACKS,
  CATALOG: [CAPABILITY_PACK.CATALOG_MAINTAIN, CAPABILITY_PACK.OFFER_PRICE, CAPABILITY_PACK.OPS_VIEW],
  INVENTORY: [CAPABILITY_PACK.STOCK_MOVE, CAPABILITY_PACK.BUY_PROCURE, CAPABILITY_PACK.OPS_VIEW],
  ACCOUNTING: [
    CAPABILITY_PACK.MONEY_READ,
    CAPABILITY_PACK.MONEY_POST,
    CAPABILITY_PACK.BUY_PAY,
    CAPABILITY_PACK.OPS_VIEW,
  ],
  SALES: [
    CAPABILITY_PACK.SELL_COMMIT,
    CAPABILITY_PACK.STOREFRONT_MAINTAIN,
    CAPABILITY_PACK.STOREFRONT_COMMERCE,
    CAPABILITY_PACK.OFFER_PROMO,
    CAPABILITY_PACK.CRM_LEAD,
    CAPABILITY_PACK.CONTENT_PUBLISH,
    CAPABILITY_PACK.OPS_VIEW,
  ],
  WEB: [
    CAPABILITY_PACK.STOREFRONT_MAINTAIN,
    CAPABILITY_PACK.CONTENT_PUBLISH,
    CAPABILITY_PACK.OFFER_PROMO,
    CAPABILITY_PACK.OPS_VIEW,
  ],
  VIEWER: [CAPABILITY_PACK.OPS_VIEW],
  SHOWROOM_STAFF: [CAPABILITY_PACK.SELL_COMMIT, CAPABILITY_PACK.OPS_VIEW],
  STOREFRONT: [CAPABILITY_PACK.OPS_VIEW],
};

export const SEEDED_ROLE_NAMES: readonly string[] = Object.freeze(Object.keys(ROLE_PACKS));

/** Pack pairs that must not co-exist on non–SUPER_ADMIN bundles (SoD light). */
export const SOD_FORBIDDEN_PAIRS: ReadonlyArray<readonly [CapabilityPack, CapabilityPack]> = [
  [CAPABILITY_PACK.BUY_PROCURE, CAPABILITY_PACK.BUY_PAY],
  [CAPABILITY_PACK.SELL_COMMIT, CAPABILITY_PACK.MONEY_POST],
];

export function isCapabilityPack(value: string): value is CapabilityPack {
  return (ALL_CAPABILITY_PACKS as readonly string[]).includes(value);
}

export function normalizeCapabilityPacks(raw: readonly string[]): CapabilityPack[] {
  const out: CapabilityPack[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const p = String(item ?? '').trim();
    if (!isCapabilityPack(p) || seen.has(p)) continue;
    seen.add(p);
    out.push(p);
  }
  return out;
}

/**
 * Resolve packs for role names.
 * `customPacksByRole` supplies packs for non-seeded roles (from DB).
 */
export function packsForRoles(
  roleNames: readonly string[],
  customPacksByRole?: Readonly<Record<string, readonly string[]>>,
): Set<CapabilityPack> {
  const out = new Set<CapabilityPack>();
  for (const name of roleNames) {
    const seeded = ROLE_PACKS[name];
    if (seeded) {
      for (const p of seeded) out.add(p);
      continue;
    }
    const custom = customPacksByRole?.[name];
    if (!custom?.length) continue;
    for (const p of normalizeCapabilityPacks(custom)) out.add(p);
  }
  return out;
}

export function roleHasPack(
  roleNames: readonly string[],
  pack: CapabilityPack,
  customPacksByRole?: Readonly<Record<string, readonly string[]>>,
): boolean {
  if (roleNames.includes('SUPER_ADMIN')) return true;
  return packsForRoles(roleNames, customPacksByRole).has(pack);
}

/** True when token/user already carries resolved packs (prefer over role-name lookup). */
export function hasPack(
  packs: readonly string[] | undefined,
  roleNames: readonly string[],
  pack: CapabilityPack,
  customPacksByRole?: Readonly<Record<string, readonly string[]>>,
): boolean {
  if (roleNames.includes('SUPER_ADMIN')) return true;
  if (packs?.length) return packs.includes(pack);
  return roleHasPack(roleNames, pack, customPacksByRole);
}

/** Returns SoD violations for a pack set (empty if SUPER_ADMIN or clean). */
export function sodViolations(
  packs: ReadonlySet<CapabilityPack>,
  opts?: { isSuperAdmin?: boolean },
): Array<{ a: CapabilityPack; b: CapabilityPack }> {
  if (opts?.isSuperAdmin) return [];
  const hits: Array<{ a: CapabilityPack; b: CapabilityPack }> = [];
  for (const [a, b] of SOD_FORBIDDEN_PAIRS) {
    if (packs.has(a) && packs.has(b)) hits.push({ a, b });
  }
  return hits;
}

/**
 * Partner pack that already blocks adding `pack` (SoD).
 * Null when `pack` is free to add (or already held).
 */
export function sodBlockingPartner(
  pack: CapabilityPack,
  selected: ReadonlySet<string>,
): CapabilityPack | null {
  for (const [a, b] of SOD_FORBIDDEN_PAIRS) {
    if (pack === a && selected.has(b)) return b;
    if (pack === b && selected.has(a)) return a;
  }
  return null;
}

/** Share of system packs held (0–100). Equal weight per pack. */
export function accessCoveragePercent(packs: readonly string[]): number {
  const total = ALL_CAPABILITY_PACKS.length;
  if (total === 0) return 0;
  const held = normalizeCapabilityPacks(packs).length;
  return Math.round((100 * held) / total);
}

export type AccessCoverageBand = 'none' | 'minimal' | 'limited' | 'extended' | 'broad' | 'full';

/** Traffic-light band: more access = hotter / more caution. */
export function accessCoverageBand(percent: number): AccessCoverageBand {
  const p = Math.max(0, Math.min(100, Math.round(percent)));
  if (p <= 0) return 'none';
  if (p <= 24) return 'minimal';
  if (p <= 49) return 'limited';
  if (p <= 74) return 'extended';
  if (p <= 99) return 'broad';
  return 'full';
}

export const ACCESS_COVERAGE_LEVEL_FR: Record<AccessCoverageBand, string> = {
  none: 'Aucun',
  minimal: 'Minimal',
  limited: 'Limité',
  extended: 'Étendu',
  broad: 'Large',
  full: 'Complet',
};
